import type { TableCellProps } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import type {
  MRT_Cell,
  MRT_Column,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowData,
  MRT_TableInstance
} from "material-react-table";
import type { VisitorConsentDocument } from "./visitor-consent-documents.context";
import { storedOrderToDisplayOrder } from "./visitor-consent-documents.context";

type MrtHeadCellPropsParams<TData extends MRT_RowData> = {
  column: MRT_Column<TData>;
  table: MRT_TableInstance<TData>;
};

type MrtBodyCellPropsParams<TData extends MRT_RowData> = {
  cell: MRT_Cell<TData, unknown>;
  column: MRT_Column<TData>;
  row: MRT_Row<TData>;
  table: MRT_TableInstance<TData>;
};

const responsiveMdTableCellSx = { display: { xs: "none", md: "table-cell" } } as const;

function resolveSx(sx: SxProps<Theme> | undefined, theme: Theme) {
  if (!sx) {
    return {};
  }
  return typeof sx === "function" ? sx(theme) : sx;
}

/** MRT merges `sx` via Object.assign — arrays are not supported; use a resolver function. */
function mergeMrtCellSx(
  globalSx: SxProps<Theme> | undefined,
  extraSx: SxProps<Theme>
): SxProps<Theme> {
  return ((theme: Theme) => ({
    ...resolveSx(globalSx, theme),
    ...responsiveMdTableCellSx,
    ...resolveSx(extraSx, theme)
  })) as SxProps<Theme>;
}

function mergeMrtHeadCellProps<TData extends MRT_RowData>(
  params: MrtHeadCellPropsParams<TData>,
  extraSx: SxProps<Theme>
): TableCellProps {
  const globalOption = params.table.options.muiTableHeadCellProps;
  const globalProps =
    typeof globalOption === "function" ? globalOption(params) : globalOption ?? {};

  return {
    ...globalProps,
    sx: mergeMrtCellSx(globalProps.sx, extraSx)
  };
}

function mergeMrtBodyCellProps<TData extends MRT_RowData>(
  params: MrtBodyCellPropsParams<TData>,
  extraSx: SxProps<Theme>
): TableCellProps {
  const globalOption = params.table.options.muiTableBodyCellProps;
  const globalProps =
    typeof globalOption === "function" ? globalOption(params) : globalOption ?? {};

  return {
    ...globalProps,
    sx: mergeMrtCellSx(globalProps.sx, extraSx)
  };
}

export const truncateConsentDocText = (value: string, max: number) =>
  value.length <= max ? value : `${value.slice(0, max)}…`;

export function renderVisitorConsentDocumentsOrderCell(storedOrder: number) {
  const displayOrder = storedOrderToDisplayOrder(storedOrder, Number.MAX_SAFE_INTEGER);
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="body2">{displayOrder}</Typography>
    </Box>
  );
}

export function renderVisitorConsentDocumentsTitleCell(title: string) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
}

export function renderVisitorConsentDocumentsDescriptionCell(description: string) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      <Typography variant="body2" color="text.secondary">
        {truncateConsentDocText(description, 140)}
      </Typography>
    </Box>
  );
}

type RenderFileCellParams = {
  fileName: string | null;
  emptyLabel: string;
};

export function renderVisitorConsentDocumentsFileCell({
  fileName,
  emptyLabel
}: RenderFileCellParams) {
  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <Typography variant="body2" color="text.secondary">
        {fileName ?? emptyLabel}
      </Typography>
    </Box>
  );
}

export const visitorConsentDocumentsDescriptionColumnLayout: Pick<
  MRT_ColumnDef<VisitorConsentDocument>,
  "muiTableHeadCellProps" | "muiTableBodyCellProps"
> = {
  muiTableHeadCellProps: params => mergeMrtHeadCellProps(params, {}),
  muiTableBodyCellProps: params => mergeMrtBodyCellProps(params, { verticalAlign: "top" })
};
